import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  Clock, Send, Wallet, Users, Receipt, Link2, Tags, ShieldCheck, Mail, Sparkles, Plus, RotateCcw, Eye, Smartphone, Lock,
  ChevronDown, Settings2,
} from 'lucide-react';
import api from '../../../utils/api';
import {
  PageHeader, Panel, Field, Input, Textarea, Select, Switch, Checkbox, Button, Banner, Pill, DataTable, Modal, ConfirmModal,
  Empty, LoadingBlock, ErrorBlock, useLoad, errMsg,
} from '../kit';
import { ReferralFormFields } from './ScReferrals';
import { CoordinatorsPanel } from './ScHours';

// Support Coordination settings: every company setting the workflows read, set by someone with
// the Admin permission. Grouped by the job they serve (a left menu on wide screens, a picker on
// phones). Each group has its own Save, so one bad value never blocks another; a group stays
// mounted once opened, so switching groups keeps unsaved changes.
//
// Address: ?section=<group> keeps the open group (older names map across); ?coordinator=<id>
// opens a coordinator's profile in the Coordinators group. "Automation & inbox" opens the
// existing tool by setting ?tool=billing, which the Support Coordination page renders.

const GROUPS = [
  { value: 'time', label: 'Time & records', icon: Clock },
  { value: 'referrals', label: 'Referrals & forms', icon: Send },
  { value: 'pay', label: 'Pay & ABA', icon: Wallet },
  { value: 'coordinators', label: 'Coordinators', icon: Users },
  { value: 'claims', label: 'Claims & invoices', icon: Receipt },
  { value: 'xero', label: 'Xero', icon: Link2 },
  { value: 'activity', label: 'Activity types', icon: Tags },
  { value: 'access', label: 'Access', icon: ShieldCheck },
  { value: 'templates', label: 'Email templates', icon: Mail },
  { value: 'automation', label: 'Automation & inbox', icon: Sparkles },
];
const ALIAS = { general: 'time', forms: 'referrals', permissions: 'access', app: 'access', logins: 'access' };
const known = (v) => GROUPS.some((g) => g.value === v);

// The server names a bad field by its key ("followup_business_days must be a whole number.");
// people see the label they typed into.
const FIELD_LABELS = {
  rounding_minutes: 'Rounding', daily_hours_flag: 'Flag a day over', retention_years: 'Keep records for',
  referral_prefix_received: 'Received referral prefix', referral_prefix_sent: 'Sent referral prefix',
  followup_business_days: 'Follow up after', form_link_expiry_days: 'Form links expire after',
  pay_period: 'Pay period', pay_period_anchor: 'A pay period starts on', bill_client_ref: 'Client on bill lines', bill_due_days: 'Bill due',
  claim_period: 'Claim period', claim_period_anchor: 'A claim period starts on', claim_age_warning_days: 'Warn about time older than',
  budget_warning_pct: 'Warn when a budget has less than', invoice_prefix: 'Invoice number prefix', invoice_terms_days: 'Invoice terms',
  overdue_reminder_days: 'Overdue reminder', xero_ndia_contact: 'NDIA contact name',
};
const friendly = (msg) => String(msg || '').replace(/\b[a-z]+(?:_[a-z0-9]+)+\b/g, (m) => FIELD_LABELS[m] || m);

export default function ScSettings({ canAdmin = true }) {
  const [sp, setSp] = useSearchParams();
  const raw = sp.get('section');
  const named = ALIAS[raw] || raw;
  let section = 'time';
  if (known(named)) section = named;
  else if (sp.get('coordinator')) section = 'coordinators';
  const go = useCallback((v) => setSp((p) => {
    const n = new URLSearchParams(p);
    n.set('section', v); n.delete('coordinator');
    return n;
  }, { replace: true }), [setSp]);
  const openTool = useCallback((t) => setSp((p) => { const n = new URLSearchParams(p); n.set('tool', t); return n; }, { replace: true }), [setSp]);
  const s = useLoad('/sc/settings');
  const [visited, setVisited] = useState(() => new Set([section]));
  useEffect(() => { setVisited((v) => (v.has(section) ? v : new Set([...v, section]))); }, [section]);

  const fromSettings = (Comp) => (s.error ? <ErrorBlock error={s.error} onRetry={s.reload} /> : !s.data ? <LoadingBlock rows={6} /> : <Comp s={s} disabled={!canAdmin} />);
  const body = (v) => {
    switch (v) {
      case 'time': return fromSettings(TimeGroup);
      case 'referrals': return <div className="space-y-5">{fromSettings(ReferralGroup)}<ReferralFormFields canAdmin={canAdmin} /></div>;
      case 'pay': return fromSettings(PayGroup);
      case 'coordinators': return <CoordinatorsPanel />;
      case 'claims': return fromSettings(ClaimsGroup);
      case 'xero': return fromSettings(XeroGroup);
      case 'activity': return <ActivityTypes disabled={!canAdmin} />;
      case 'access': return canAdmin ? <Access onOpenTool={openTool} /> : <AdminOnly />;
      case 'templates': return canAdmin ? <Templates /> : <AdminOnly />;
      case 'automation': return <Automation onOpenTool={openTool} />;
      default: return null;
    }
  };

  return (
    <div>
      <PageHeader title="Settings" sub="Company settings for Support Coordination; only people with the Admin permission can change them." />
      {!canAdmin && <div className="mb-5"><Banner tone="amber" title="Read only">You need the Admin permission to change these settings.</Banner></div>}
      <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-5 lg:gap-7 items-start">
        <nav aria-label="Settings groups" className="hidden lg:block lg:sticky lg:top-4">
          <ul className="space-y-0.5">
            {GROUPS.map((g) => {
              const on = g.value === section;
              return (
                <li key={g.value}>
                  <button type="button" onClick={() => go(g.value)} aria-current={on ? 'page' : undefined}
                    className={clsx('w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13.5px] font-medium text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                      on ? 'bg-violet-50 text-violet-800 font-semibold' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900')}>
                    <g.icon size={15} className={on ? 'text-violet-600' : 'text-slate-500'} aria-hidden />
                    {g.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="lg:hidden">
          <Field label="Settings group">
            <Select value={section} onChange={go} options={GROUPS.map((g) => ({ value: g.value, label: g.label }))} />
          </Field>
        </div>
        <div className="min-w-0">
          {GROUPS.filter((g) => visited.has(g.value)).map((g) => (
            <div key={g.value} hidden={g.value !== section}>{body(g.value)}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AdminOnly() {
  return (
    <Panel>
      <Empty icon={Lock} title="Only admins can see this">Ask someone with the Admin permission to change it.</Empty>
    </Panel>
  );
}

// ── A group of settings with its own draft and Save ────────────────────────────────────────
function useGroup(s, keys) {
  const pick = () => Object.fromEntries(keys.map((k) => [k, s.data?.[k] ?? '']));
  const [draft, setDraft] = useState(pick);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const key = JSON.stringify(pick());
  useEffect(() => { setDraft(JSON.parse(key)); setError(''); }, [key]);
  const dirty = JSON.stringify(draft) !== key;
  const set = (k) => (v) => { setError(''); setDraft((d) => ({ ...d, [k]: v && v.target ? v.target.value : v })); };
  const reset = () => { setDraft(JSON.parse(key)); setError(''); };
  const save = async () => {
    setBusy(true); setError('');
    try {
      const r = await api.put('/sc/settings', draft);
      s.setData(r.data);
      toast.success('Settings saved');
    } catch (e) { setError(friendly(errMsg(e))); } finally { setBusy(false); }
  };
  return { draft, set, save, reset, busy, dirty, error };
}

function SaveBar({ g, disabled }) {
  let state = 'All changes saved';
  if (disabled) state = 'Read only';
  else if (g.dirty) state = 'Unsaved changes';
  return (
    // Sticky from tablet width up; on a phone the app's bottom navigation owns the bottom edge.
    <div className="md:sticky md:bottom-4 z-10 rounded-2xl border border-slate-200/80 bg-white/95 backdrop-blur px-4 py-3 space-y-2">
      {g.error && <p role="alert" className="text-[13px] text-rose-700">{g.error}</p>}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <span className={clsx('mr-auto text-[12.5px] font-medium', g.dirty && !disabled ? 'text-amber-800' : 'text-slate-500')} aria-live="polite">{state}</span>
        {g.dirty && !disabled && <Button variant="ghost" onClick={g.reset}>Discard</Button>}
        <Button onClick={g.save} loading={g.busy} disabled={disabled || !g.dirty}>Save changes</Button>
      </div>
    </div>
  );
}

const Grid = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;

// ── Time & records ──────────────────────────────────────────────────────────────────────────
function TimeGroup({ s, disabled }) {
  const g = useGroup(s, ['rounding_minutes', 'daily_hours_flag', 'retention_years', 'clockin_fallback']);
  return (
    <div className="space-y-5">
      <Panel title="Time entries" icon={Clock} sub="One rounding rule for both pay and claims.">
        <Grid>
          <Field label="Round each entry to" hint="New entries round to the nearest step, with at least one step for any time worked; saved entries keep their minutes.">
            <Select value={String(g.draft.rounding_minutes || 1)} onChange={g.set('rounding_minutes')} disabled={disabled}
              options={[{ value: '1', label: 'Exact minutes' }, { value: '6', label: '6 minutes (0.1 hour)' }, { value: '15', label: '15 minutes (0.25 hour)' }]} />
          </Field>
          <Field label="Flag a day over (hours)" hint="A coordinator's day above this many hours shows a warning on SC hours and the Overview. The time still saves.">
            <Input type="number" min="0" step="0.5" value={g.draft.daily_hours_flag ?? ''} onChange={g.set('daily_hours_flag')} disabled={disabled} />
          </Field>
          <Field label="Keep records for (years)" hint="Records are kept this long and never hard deleted; the NDIA asks for 7 years.">
            <Input type="number" min="1" max="50" value={g.draft.retention_years ?? ''} onChange={g.set('retention_years')} disabled={disabled} />
          </Field>
        </Grid>
      </Panel>
      <Panel title="Schedule Board clock-in" icon={Settings2} sub="Where a worker clocks in when a shift has no shift address and no linked house.">
        <div className="max-w-md">
          <Field label="If a shift has no address">
            <Select value={g.draft.clockin_fallback || 'client_address'} onChange={g.set('clockin_fallback')} disabled={disabled}
              options={[{ value: 'client_address', label: "Use the client's address", hint: 'Workers clock in at the client’s address.' },
                { value: 'block', label: 'Block clock-in', hint: 'A shift needs a shift address or a house before it can be saved.' }]} />
          </Field>
        </div>
      </Panel>
      <SaveBar g={g} disabled={disabled} />
    </div>
  );
}

// ── Referrals & forms ──────────────────────────────────────────────────────────────────────
function ReferralGroup({ s, disabled }) {
  const g = useGroup(s, ['referral_prefix_received', 'referral_prefix_sent', 'followup_business_days', 'form_link_expiry_days']);
  return (
    <div className="space-y-5">
      <Panel title="Referral numbers and follow-up" icon={Send} sub="How referrals are numbered and when they come up for follow-up.">
        <Grid>
          <Field label="Received referral prefix" hint={`Numbers look like ${g.draft.referral_prefix_received || 'RR'}-00012.`}>
            <Input value={g.draft.referral_prefix_received || ''} onChange={g.set('referral_prefix_received')} maxLength={10} disabled={disabled} />
          </Field>
          <Field label="Sent referral prefix" hint={`Numbers look like ${g.draft.referral_prefix_sent || 'RS'}-00012.`}>
            <Input value={g.draft.referral_prefix_sent || ''} onChange={g.set('referral_prefix_sent')} maxLength={10} disabled={disabled} />
          </Field>
          <Field label="Follow up after (business days)" hint="Sets the next follow-up date on each new referral and after each follow-up.">
            <Input type="number" min="0" value={g.draft.followup_business_days ?? ''} onChange={g.set('followup_business_days')} disabled={disabled} />
          </Field>
          <Field label="Form links expire after (days)" hint="Online form links stop working after this many days.">
            <Input type="number" min="1" value={g.draft.form_link_expiry_days ?? ''} onChange={g.set('form_link_expiry_days')} disabled={disabled} />
          </Field>
        </Grid>
      </Panel>
      <SaveBar g={g} disabled={disabled} />
    </div>
  );
}

// ── Pay & ABA ───────────────────────────────────────────────────────────────────────────────
const BANK_FIELDS = ['aba_fi_abbrev', 'aba_user_name', 'aba_apca_id', 'aba_bsb', 'aba_account_number', 'aba_account_name'];
function PayGroup({ s, disabled }) {
  // Without Admin, Generate pay files or Bank details the server leaves the bank fields out
  // (it sends aba_set instead), so they are never part of this draft.
  const bankHidden = !!s.data && Object.prototype.hasOwnProperty.call(s.data, 'aba_set');
  const keys = ['pay_period', 'pay_period_anchor', 'pay_non_billable', 'bill_client_ref', 'bill_due_days', 'aba_description', 'aba_balancing',
    ...(bankHidden ? [] : BANK_FIELDS)];
  const g = useGroup(s, keys);
  return (
    <div className="space-y-5">
      <Panel title="Pay periods and pay" icon={Wallet} sub="How long a pay period is and which saved time is paid.">
        <Grid>
          <Field label="Pay period">
            <Select value={g.draft.pay_period || 'fortnightly'} onChange={g.set('pay_period')} disabled={disabled}
              options={[{ value: 'weekly', label: 'Weekly' }, { value: 'fortnightly', label: 'Fortnightly' }]} />
          </Field>
          <Field label="A pay period starts on" hint="Any date a pay period started; the rest follow on from it.">
            <Input type="date" value={g.draft.pay_period_anchor || ''} onChange={g.set('pay_period_anchor')} disabled={disabled} />
          </Field>
        </Grid>
        <div className="mt-5 space-y-4">
          <Switch label="Pay non-billable time" hint="When off, only billable activity types are paid." checked={!!g.draft.pay_non_billable} onChange={g.set('pay_non_billable')} disabled={disabled} />
        </div>
      </Panel>
      <Panel title="Contractor bills" icon={Receipt} sub="What goes on the bill each contractor is paid by.">
        <Grid>
          <Field label="Client on bill lines" hint="Client ID keeps participants' names off bills.">
            <Select value={g.draft.bill_client_ref || 'id'} onChange={g.set('bill_client_ref')} disabled={disabled}
              options={[{ value: 'id', label: 'Client ID' }, { value: 'name', label: 'Client name' }]} />
          </Field>
          <Field label="Bill due (days after the pay period ends)">
            <Input type="number" min="0" value={g.draft.bill_due_days ?? ''} onChange={g.set('bill_due_days')} disabled={disabled} />
          </Field>
        </Grid>
      </Panel>
      <Panel title="ABA payment file" icon={ShieldCheck} sub="Your bank's details for the file that pays contractors; Rostery never emails it.">
        {bankHidden ? (
          <Banner tone="blue" title={s.data.aba_set ? 'The paying account is set up' : 'The paying account isn’t set up yet'}>
            Only people with the Admin, Generate pay files or Bank details permission can see the bank details.
          </Banner>
        ) : (
          <Grid>
            <Field label="Financial institution" hint="3 letters, for example CBA, WBC, NAB or ANZ."><Input value={g.draft.aba_fi_abbrev || ''} onChange={g.set('aba_fi_abbrev')} maxLength={3} disabled={disabled} /></Field>
            <Field label="APCA user ID" hint="6 digits, from your bank."><Input inputMode="numeric" value={g.draft.aba_apca_id || ''} onChange={g.set('aba_apca_id')} maxLength={6} disabled={disabled} /></Field>
            <Field label="User name" hint="Up to 26 characters, as registered with your bank."><Input value={g.draft.aba_user_name || ''} onChange={g.set('aba_user_name')} maxLength={26} disabled={disabled} /></Field>
            <Field label="Paying account BSB" hint="000-000"><Input inputMode="numeric" placeholder="000-000" value={g.draft.aba_bsb || ''} onChange={g.set('aba_bsb')} disabled={disabled} /></Field>
            <Field label="Paying account number" hint="Up to 9 digits."><Input inputMode="numeric" value={g.draft.aba_account_number || ''} onChange={g.set('aba_account_number')} disabled={disabled} /></Field>
            <Field label="Paying account name" hint="Up to 32 characters."><Input value={g.draft.aba_account_name || ''} onChange={g.set('aba_account_name')} maxLength={32} disabled={disabled} /></Field>
          </Grid>
        )}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="File description" hint="Up to 12 characters, for example SC PAY."><Input value={g.draft.aba_description || ''} onChange={g.set('aba_description')} maxLength={12} disabled={disabled} /></Field>
        </div>
        <div className="mt-4">
          <Switch label="Add a balancing record" hint="Some banks need a self-balancing file, so ask your bank before turning this on." checked={!!g.draft.aba_balancing} onChange={g.set('aba_balancing')} disabled={disabled} />
        </div>
      </Panel>
      <SaveBar g={g} disabled={disabled} />
    </div>
  );
}

// ── Claims & invoices ──────────────────────────────────────────────────────────────────────
function ClaimsGroup({ s, disabled }) {
  const g = useGroup(s, ['claim_period', 'claim_period_anchor', 'claim_age_warning_days', 'budget_warning_pct',
    'invoice_prefix', 'invoice_terms_days', 'overdue_reminder_days']);
  return (
    <div className="space-y-5">
      <Panel title="Claim periods and checks" icon={Receipt} sub="When time is claimed and what is checked first.">
        <Grid>
          <Field label="Claim period">
            <Select value={g.draft.claim_period || 'weekly'} onChange={g.set('claim_period')} disabled={disabled}
              options={[{ value: 'weekly', label: 'Weekly' }, { value: 'fortnightly', label: 'Fortnightly' }, { value: 'monthly', label: 'Monthly' }]} />
          </Field>
          <Field label="A claim period starts on" hint="Any date a claim period started; the rest follow on from it.">
            <Input type="date" value={g.draft.claim_period_anchor || ''} onChange={g.set('claim_period_anchor')} disabled={disabled} />
          </Field>
          <Field label="Warn about time older than (days)" hint="Unclaimed or rejected time this old is flagged; anything over 90 days is escalated.">
            <Input type="number" min="1" value={g.draft.claim_age_warning_days ?? ''} onChange={g.set('claim_age_warning_days')} disabled={disabled} />
          </Field>
          <Field label="Warn when a budget has less than (% left)" hint="Applies to each support coordination budget.">
            <Input type="number" min="0" max="100" value={g.draft.budget_warning_pct ?? ''} onChange={g.set('budget_warning_pct')} disabled={disabled} />
          </Field>
        </Grid>
        <p className="mt-4 text-[12.5px] text-slate-500">All saved billable time can be claimed, subject to the checks above. Downloading the claim file or invoice locks the time on its entries.</p>
      </Panel>
      <Panel title="Invoices" icon={Mail} sub="Invoices for plan-managed and self-managed participants.">
        <Grid>
          <Field label="Invoice number prefix" hint={`Numbers look like ${g.draft.invoice_prefix || 'SCI'}00042; a reissue adds -R1.`}>
            <Input value={g.draft.invoice_prefix || ''} onChange={g.set('invoice_prefix')} maxLength={10} disabled={disabled} />
          </Field>
          <Field label="Invoice terms (days)"><Input type="number" min="0" value={g.draft.invoice_terms_days ?? ''} onChange={g.set('invoice_terms_days')} disabled={disabled} /></Field>
          <Field label="Overdue reminder (days after the due date)"><Input type="number" min="0" value={g.draft.overdue_reminder_days ?? ''} onChange={g.set('overdue_reminder_days')} disabled={disabled} /></Field>
        </Grid>
      </Panel>
      <SaveBar g={g} disabled={disabled} />
    </div>
  );
}

// ── Xero ────────────────────────────────────────────────────────────────────────────────────
function XeroGroup({ s, disabled }) {
  const g = useGroup(s, ['xero_sales_account', 'xero_sales_tax_gst', 'xero_sales_tax_free', 'xero_ndia_contact', 'xero_expense_account',
    'xero_tax_gst', 'xero_tax_nongst', 'xero_tracking_category', 'xero_tracking_option']);
  return (
    <div className="space-y-5">
      <Panel title="Contractor bills" icon={Wallet} sub="The codes on contractor bills, downloaded for Xero or sent to Xero as drafts; each must match Xero exactly.">
        <Grid>
          <Field label="Expense account code"><Input value={g.draft.xero_expense_account || ''} onChange={g.set('xero_expense_account')} disabled={disabled} /></Field>
          <Field label="Tax rate when registered for GST"><Input value={g.draft.xero_tax_gst || ''} onChange={g.set('xero_tax_gst')} placeholder="GST on Expenses" disabled={disabled} /></Field>
          <Field label="Tax rate when not registered"><Input value={g.draft.xero_tax_nongst || ''} onChange={g.set('xero_tax_nongst')} placeholder="EXEMPTEXPENSES" disabled={disabled} /></Field>
          <Field label="Tracking category (optional)"><Input value={g.draft.xero_tracking_category || ''} onChange={g.set('xero_tracking_category')} disabled={disabled} /></Field>
          <Field label="Tracking option (optional)"><Input value={g.draft.xero_tracking_option || ''} onChange={g.set('xero_tracking_option')} disabled={disabled} /></Field>
        </Grid>
      </Panel>
      <Panel title="Claims and invoices" icon={Receipt} sub="The codes on claim batches and invoices exported for Xero.">
        <Grid>
          <Field label="Sales account code"><Input value={g.draft.xero_sales_account || ''} onChange={g.set('xero_sales_account')} disabled={disabled} /></Field>
          <Field label="NDIA contact name" hint="The Xero contact NDIA-managed claims are raised against."><Input value={g.draft.xero_ndia_contact || ''} onChange={g.set('xero_ndia_contact')} disabled={disabled} /></Field>
          <Field label="Tax rate for GST on income"><Input value={g.draft.xero_sales_tax_gst || ''} onChange={g.set('xero_sales_tax_gst')} placeholder="GST on Income" disabled={disabled} /></Field>
          <Field label="Tax rate for GST-free income"><Input value={g.draft.xero_sales_tax_free || ''} onChange={g.set('xero_sales_tax_free')} placeholder="GST Free Income" disabled={disabled} /></Field>
        </Grid>
      </Panel>
      <SaveBar g={g} disabled={disabled} />
    </div>
  );
}

// ── Activity types ──────────────────────────────────────────────────────────────────────────
const CLAIM_TYPES = [
  { value: 'NF2F', label: 'Non-face-to-face', code: 'NF2F' }, { value: 'TRAN', label: 'Provider travel', code: 'TRAN' },
  { value: 'REPW', label: 'NDIA report writing', code: 'REPW' }, { value: 'THLT', label: 'Telehealth', code: 'THLT' },
  { value: 'CANC', label: 'Short-notice cancellation', code: 'CANC' },
];
const claimLabel = (v) => { const t = CLAIM_TYPES.find((x) => x.value === v); return t ? t.label : 'Direct service'; };

function ActivityTypes({ disabled }) {
  const l = useLoad('/sc/activity-types', { all: '1' });
  const [edit, setEdit] = useState(null);
  const cols = useMemo(() => [
    {
      accessorKey: 'name', header: 'Activity type',
      cell: ({ row }) => (disabled ? <span className="font-semibold text-slate-900">{row.original.name}</span> : (
        <button type="button" onClick={(e) => { e.stopPropagation(); setEdit(row.original); }}
          className="font-semibold text-slate-900 hover:text-indigo-700 text-left rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
          {row.original.name}
        </button>
      )),
    },
    {
      accessorKey: 'claim_type', header: 'Claimed as',
      cell: ({ getValue }) => (
        <span>{claimLabel(getValue())}{getValue() ? <span className="ml-1.5 font-mono text-[12px] text-slate-500">{getValue()}</span> : null}</span>
      ),
    },
    { accessorKey: 'billable_default', header: 'Billable', cell: ({ getValue }) => (getValue() ? <Pill tone="emerald" label="Billable" /> : <Pill tone="slate" label="Non-billable" />) },
    { accessorKey: 'is_paid', header: 'Coordinator paid', cell: ({ getValue }) => (getValue() ? 'Paid' : 'Not paid') },
    { accessorKey: 'is_active', header: 'Status', cell: ({ getValue }) => (getValue() ? <Pill tone="emerald" label="In use" /> : <Pill tone="slate" label="Retired" />) },
  ], [disabled]);
  return (
    <Panel title="Activity types" icon={Tags} pad={false}
      sub="What a time entry was for: how it's claimed, whether it's billable by default and whether the coordinator is paid for it."
      actions={!disabled && <Button size="sm" icon={Plus} onClick={() => setEdit({ name: '', claim_type: '', billable_default: true, is_paid: true, is_active: true })}>Add type</Button>}>
      <div className="px-1 pb-2">
        <DataTable columns={cols} data={l.data} loading={l.loading} error={l.error} onRetry={l.reload} onRowClick={disabled ? undefined : (r) => setEdit(r)} dense
          empty={<Empty icon={Tags} title="No activity types yet" action={!disabled && <Button size="sm" icon={Plus} onClick={() => setEdit({ name: '', claim_type: '', billable_default: true, is_paid: true, is_active: true })}>Add type</Button>}>Add the kinds of work coordinators log time for.</Empty>} />
      </div>
      <ActivityModal value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); l.reload(); }} />
    </Panel>
  );
}

function ActivityModal({ value, onClose, onSaved }) {
  const [f, setF] = useState(value || {});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { setF(value || {}); setError(''); }, [value]);
  const save = async () => {
    if (!String(f.name || '').trim()) { setError('Give the activity type a name.'); return; }
    setBusy(true); setError('');
    try {
      if (f.id) await api.put(`/sc/activity-types/${f.id}`, f); else await api.post('/sc/activity-types', f);
      toast.success('Activity type saved'); onSaved();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open={!!value} onClose={onClose} title={f.id ? 'Edit activity type' : 'New activity type'}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={save}>Save activity type</Button></>}>
      <div className="space-y-4">
        <Field label="Name" required error={error && !String(f.name || '').trim() ? error : undefined}>
          <Input value={f.name || ''} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus />
        </Field>
        <Field label="Claimed as" hint="Direct service unless the time is one of these NDIA claim types.">
          <Select value={f.claim_type || ''} onChange={(v) => setF({ ...f, claim_type: v })} allowEmpty emptyLabel="Direct service"
            options={CLAIM_TYPES.map((t) => ({ value: t.value, label: `${t.label} (${t.code})` }))} />
        </Field>
        <Switch label="Billable by default" checked={f.billable_default !== false} onChange={(v) => setF({ ...f, billable_default: v })} />
        <Switch label="Coordinator is paid for this time" checked={f.is_paid !== false} onChange={(v) => setF({ ...f, is_paid: v })} />
        {f.id && <Switch label="In use" hint="Retire a type to hide it from new entries; saved entries keep it." checked={f.is_active !== false} onChange={(v) => setF({ ...f, is_active: v })} />}
        {error && String(f.name || '').trim() && <Banner tone="red" title="Not saved">{error}</Banner>}
      </div>
    </Modal>
  );
}

// ── Access: permissions and coordinator app logins ──────────────────────────────────────────
function Access({ onOpenTool }) {
  const perms = useLoad('/sc/permissions');
  return (
    <div className="space-y-5">
      <Permissions l={perms} />
      <AppLogins users={perms} onOpenTool={onOpenTool} />
    </div>
  );
}

function Permissions({ l }) {
  const [busy, setBusy] = useState(null);
  const [help, setHelp] = useState(false);
  if (l.error) return <ErrorBlock error={l.error} onRetry={l.reload} />;
  if (!l.data) return <LoadingBlock rows={5} />;
  const { catalogue, users } = l.data;
  const toggle = async (u, key, on) => {
    const next = on ? [...new Set([...u.permissions, key])] : u.permissions.filter((p) => p !== key);
    setBusy(`${u.id}:${key}`);
    try {
      await api.put(`/sc/permissions/${u.id}`, { permissions: next });
      l.setData((d) => ({ ...d, users: d.users.map((x) => (x.id === u.id ? { ...x, permissions: next, custom: true } : x)) }));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const who = (u) => {
    if (u.isAdmin) return 'Admin: has every permission';
    return u.custom ? 'Office user · set here' : 'Office user · starting permissions';
  };
  return (
    <Panel title="Permissions" icon={ShieldCheck} pad={false}
      sub="What each office user can do in Support Coordination; changes save as you tick.">
      <div className="px-4 pb-2">
        <Button size="sm" variant="ghost" icon={ChevronDown} onClick={() => setHelp((h) => !h)} aria-expanded={help} aria-controls="scs-perm-help">
          {help ? 'Hide what each permission allows' : 'What each permission allows'}
        </Button>
      </div>
      {help && (
        <dl id="scs-perm-help" className="mx-5 mb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 rounded-xl bg-slate-50 px-4 py-3">
          {catalogue.map((p) => (
            <div key={p.key}>
              <dt className="text-[12.5px] font-semibold text-slate-800">{p.label}</dt>
              <dd className="text-[12.5px] text-slate-600">{p.help}</dd>
            </div>
          ))}
        </dl>
      )}
      {!users.length ? <Empty icon={Users} title="No office users">Add staff with the Admin or Office role to give them permissions here.</Empty> : (
        <div className="overflow-x-auto pb-2">
          <table className="w-full text-[12.5px] border-separate border-spacing-0">
            <caption className="sr-only">Support Coordination permissions for each office user</caption>
            <thead>
              <tr>
                <th scope="col" className="sticky left-0 z-[1] bg-slate-50 text-left font-semibold text-slate-600 px-4 py-2.5 border-y border-slate-200/80 min-w-[190px]">User</th>
                {catalogue.map((p) => (
                  <th key={p.key} scope="col" title={p.help} className="bg-slate-50 font-semibold text-slate-600 px-2 py-2.5 border-y border-slate-200/80 text-center whitespace-nowrap">{p.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <th scope="row" className="sticky left-0 z-[1] bg-white text-left px-4 py-2.5 border-b border-slate-100">
                    <span className="font-semibold text-slate-900">{u.name}</span>
                    <span className="block text-[12px] font-normal text-slate-500">{who(u)}</span>
                  </th>
                  {catalogue.map((p) => (
                    <td key={p.key} className="text-center px-2 py-2.5 border-b border-slate-100">
                      <div className="inline-flex">
                        <Checkbox aria-label={`${u.name}: ${p.label}`} checked={u.permissions.includes(p.key)} disabled={u.isAdmin || busy === `${u.id}:${p.key}`}
                          onChange={(v) => toggle(u, p.key, v)} />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function AppLogins({ users, onOpenTool }) {
  const accounts = useLoad('/sc/app-accounts');
  const opts = (users.data?.users || []).map((u) => ({ value: u.id, label: u.name, hint: u.email }));
  const link = async (a, uid) => {
    try { await api.put(`/sc/app-accounts/${a.id}/link-user`, { user_id: uid || null }); toast.success(uid ? 'App login linked' : 'Link removed'); accounts.reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const create = <Button size="sm" variant="secondary" icon={Smartphone} onClick={() => onOpenTool('logins')}>Create or remove logins</Button>;
  return (
    <Panel title="Coordinator app logins" icon={Smartphone}
      sub="Each app login logs time as one staff member, so their hours, pay and claims stay together; an app login never gets pay, bank, claiming or admin access.">
      {accounts.error ? <ErrorBlock error={accounts.error} onRetry={accounts.reload} /> : !accounts.data ? <LoadingBlock rows={3} /> : !accounts.data.length ? (
        <Empty icon={Smartphone} title="No app logins yet" action={create}>Create a login so a coordinator can log time from the Rostery coordinator app.</Empty>
      ) : (
        <>
        <div className="mb-3">{create}</div>
        <ul className="divide-y divide-slate-100 border-y border-slate-100">
          {accounts.data.map((a) => (
            <li key={a.id} className="flex flex-wrap items-end gap-3 py-3">
              <div className="flex-1 min-w-[200px]">
                <p className="text-[13.5px] font-semibold text-slate-900">{a.first_name} {a.last_name}</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-2 text-[12.5px] text-slate-500">{a.email} <Pill size="xs" status={a.status} />
                  {!a.user_id && <Pill size="xs" tone="amber" label="Can't log time until linked" />}
                </p>
              </div>
              <div className="w-full sm:w-72">
                <Field label="Logs time as">
                  <Select value={a.user_id || ''} onChange={(v) => link(a, v)} options={opts} allowEmpty emptyLabel="Not linked" placeholder="Choose a staff member" />
                </Field>
              </div>
            </li>
          ))}
        </ul>
        </>
      )}
    </Panel>
  );
}

// ── Email templates ─────────────────────────────────────────────────────────────────────────
function Templates() {
  const l = useLoad('/sc/templates');
  const [kind, setKind] = useState(null);
  const list = useMemo(() => l.data || [], [l.data]);
  const cur = list.find((t) => t.kind === kind) || list[0];
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [resetting, setResetting] = useState(false);
  useEffect(() => { if (cur) setDraft({ subject: cur.subject, body: cur.body, enabled: cur.enabled }); }, [cur]);
  if (l.error) return <ErrorBlock error={l.error} onRetry={l.reload} />;
  if (!l.data) return <LoadingBlock rows={6} />;
  if (!list.length) return <Panel><Empty icon={Mail} title="No email templates">There are no emails to set up.</Empty></Panel>;
  const groups = [...new Set(list.map((t) => t.group))];
  const dirty = !!(cur && draft) && (draft.subject !== cur.subject || draft.body !== cur.body || (draft.enabled !== false) !== (cur.enabled !== false));
  const save = async () => {
    setBusy('save');
    try { await api.put(`/sc/templates/${cur.kind}`, draft); toast.success('Template saved'); l.reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(''); }
  };
  const reset = async () => { await api.post(`/sc/templates/${cur.kind}/reset`); toast.success('Template reset to the standard wording'); l.reload(); };
  const doPreview = async () => {
    setBusy('preview');
    try {
      if (dirty) await api.put(`/sc/templates/${cur.kind}`, draft);
      const r = await api.post(`/sc/templates/${cur.kind}/preview`);
      setPreview(r.data);
      if (dirty) l.reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(''); }
  };
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[240px_minmax(0,1fr)] gap-5 items-start">
      <nav aria-label="Email templates" className="hidden xl:block bg-white rounded-2xl border border-slate-200/80 p-2">
        {groups.map((gname) => (
          <div key={gname} className="mb-1">
            <p className="px-3 pt-2 pb-1 text-[12px] font-semibold text-slate-500">{gname}</p>
            {list.filter((t) => t.group === gname).map((t) => {
              const on = cur && cur.kind === t.kind;
              return (
                <button key={t.kind} type="button" onClick={() => setKind(t.kind)} aria-current={on ? 'true' : undefined}
                  className={clsx('w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                    on ? 'bg-violet-50 text-violet-800' : 'text-slate-600 hover:bg-slate-50')}>
                  <span className="min-w-0">{t.label}</span>
                  {t.customised && <Pill size="xs" tone="violet" label="Edited" />}
                </button>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="xl:hidden">
        <Field label="Email">
          <Select value={cur ? cur.kind : ''} onChange={setKind}
            options={list.map((t) => ({ value: t.kind, label: `${t.group}: ${t.label}${t.customised ? ' (edited)' : ''}` }))} />
        </Field>
      </div>
      {cur && draft && (
        <Panel title={cur.label} icon={Mail} sub={cur.customised ? 'Edited from the standard wording.' : 'The standard wording.'}>
          <div className="space-y-4">
            <Field label="Subject"><Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} /></Field>
            <Field label="Message" hint="Plain text; links become buttons, and your logo and company details are added for you.">
              <Textarea rows={12} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} className="font-mono text-[12.5px]" />
            </Field>
            {(cur.placeholders || []).length > 0 && (
              <div>
                <p className="text-[12.5px] font-semibold text-slate-700">Fill-ins you can use</p>
                <p className="mt-1 flex flex-wrap gap-1.5">
                  {cur.placeholders.map((p) => <code key={p} className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[12px] text-slate-700">{`{{${p}}}`}</code>)}
                </p>
              </div>
            )}
            <Switch label="Send this email" hint="When off, Rostery records the event but sends nothing." checked={draft.enabled !== false} onChange={(v) => setDraft({ ...draft, enabled: v })} />
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-4 mt-5 border-t border-slate-100">
            <Button size="sm" variant="ghost" icon={Eye} loading={busy === 'preview'} onClick={doPreview}>Preview</Button>
            {cur.customised && <Button size="sm" variant="ghost" icon={RotateCcw} onClick={() => setResetting(true)}>Reset to standard</Button>}
            <span className="ml-auto flex items-center gap-2">
              {dirty && <span className="text-[12.5px] font-medium text-amber-800">Unsaved changes</span>}
              <Button onClick={save} loading={busy === 'save'} disabled={!dirty}>Save template</Button>
            </span>
          </div>
        </Panel>
      )}
      <Modal open={!!preview} onClose={() => setPreview(null)} title={preview?.subject || 'Preview'} sub="With sample details filled in." width="lg">
        {/* The server renders this from the saved template with sample values; it is our own markup. */}
        <iframe title="Email preview" className="w-full h-[480px] rounded-xl ring-1 ring-slate-200" sandbox="" srcDoc={preview?.html || ''} />
      </Modal>
      <ConfirmModal open={resetting} onClose={() => setResetting(false)} title="Reset to the standard wording?" confirmLabel="Reset template" danger onConfirm={reset}>
        Your changes to “{cur?.label}” are replaced with the standard subject and message.
      </ConfirmModal>
    </div>
  );
}

// ── Automation & inbox: the existing tool opens over this screen ───────────────────────────
function Automation({ onOpenTool }) {
  return (
    <Panel title="Automation and email inbox" icon={Sparkles}
      sub="Automatic reminders, tasks and statements, the inbox that turns emails into notes, the referral intake link and the calendar feed.">
      <Button icon={Sparkles} onClick={() => onOpenTool('billing')}>Open automation and inbox settings</Button>
    </Panel>
  );
}
